import { useFocusEffect } from "@react-navigation/native";
import { Page } from "@towersystems/roam-common-native";
import {
  createElement,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  InteractionManager,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native";
import { ScannerState } from "../../utilities/barcode-scanner/constants";
import { useStreamProvider } from "../../utilities/barcode-scanner/hooks";
import { PermissionsManager } from "../../utilities/permissions";
import { PermissionToken } from "../../utilities/permissions/constants";
import { CameraHeader, OpaqueOverlay } from "../CameraViewFinder/components";
import { PermissionGuard } from "../PermissionGuard";
import { BarcodeScannerProps } from "./types";

export function BarcodeScanner({
  onBarCodeScanned,
  headerProps,
  previewElement,
  overlayElement,
  scannableArea,
}: BarcodeScannerProps) {
  const { videoRef, scannerController, isCameraReady, cleanupStream } =
    useStreamProvider();
  const [isBlocked, setIsBlocked] = useState<boolean>(true);
  const [isDoneAnimating, setIsDoneAnimating] = useState<boolean>(false);

  const scannerStateRef = useRef(scannerController.state);
  scannerStateRef.current = scannerController.state;

  const isBlockedRef = useRef(isBlocked);
  isBlockedRef.current = isBlocked;

  const isDoneAnimatingRef = useRef(isDoneAnimating);
  isDoneAnimatingRef.current = isDoneAnimating;

  function start() {
    if (!isBlockedRef.current && isDoneAnimatingRef.current) {
      scannerController.streamCamVideo();
    }
  }

  useEffect(() => {
    if (!isCameraReady) return;
    scannerController.startScanning({ onBarCodeScanned, scannableArea });
  }, [isCameraReady]);

  useEffect(() => {
    start();
  }, [isBlocked, isDoneAnimating]);

  useEffect(() => {
    PermissionsManager.getManagedPermission(PermissionToken.Camera)
      .getPermissionResponse()
      .then((permissionResponse) => {
        setIsBlocked(!permissionResponse.granted);
      })
      .catch(() => undefined);

    const cleanup = PermissionsManager.getManagedPermission(
      PermissionToken.Camera
    ).addOnChangeListener((permitted) => {
      if (permitted) {
        setIsBlocked(false);
      }
    });
    return () => {
      cleanup();
      cleanupStream();
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      InteractionManager.runAfterInteractions(() => {
        setIsDoneAnimating(true);
      });

      if (scannerStateRef.current === ScannerState.PAUSED) {
        scannerController.resume();
      } else if (scannerStateRef.current === ScannerState.STOPPED) {
        start();
      }

      return () => {
        scannerController.pause();
      };
    }, [])
  );

  const overlayMarkup = (
    <OpaqueOverlay
      showChildren={
        isCameraReady && scannerController.state !== ScannerState.STOPPED
      }
    >
      {overlayElement}
    </OpaqueOverlay>
  );

  return (
    <Page scrollable={false} padding={0} fullWidth>
      <PermissionGuard
        allowScreen={true}
        permissions={[PermissionToken.Camera]}
      >
        <CameraHeader {...headerProps} />
        {previewElement && (
          <View style={styles.bringToFront}>{previewElement}</View>
        )}
        {isBlocked ? (
          overlayMarkup
        ) : (
          <Target
            ref={scannerController.targetRef}
            style={styles.videoContainerStyle}
          >
            <Video
              ref={videoRef}
              playsInline
              webkit-playsinline="true"
              style={styles.videoStyle}
            />
            <Canvas className="drawingBuffer" style={styles.videoStyle} />
            <View
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
              }}
            >
              {overlayMarkup}
            </View>
          </Target>
        )}
      </PermissionGuard>
    </Page>
  );
}

const Target = forwardRef(
  (
    //@ts-ignore
    props: React.ComponentProps<typeof View> & {
      children?: React.ReactNode;
      style?: StyleProp<ViewStyle>;
    },
    ref: React.Ref<HTMLDivElement>
  ) => {
    return createElement("div", { ...props, ref });
  }
);

const Video = forwardRef(
  (
    //@ts-ignore
    props: React.ComponentProps<typeof View> & {
      autoPlay?: boolean;
      playsInline?: boolean;
      muted?: boolean;
      poster?: string;
      style?: StyleProp<ViewStyle>;
    },
    ref: React.Ref<HTMLVideoElement>
  ) => {
    return createElement("video", { ...props, ref });
  }
);

const Canvas = forwardRef(
  (
    //@ts-ignore
    props: React.ComponentProps<typeof View> & {
      children?: React.ReactNode;
      className?: string;
      style?: StyleProp<ViewStyle>;
    },
    ref: React.Ref<HTMLCanvasElement>
  ) => {
    return createElement("canvas", { ...props, ref });
  }
);

const styles = StyleSheet.create({
  videoContainerStyle: {
    position: "absolute",
    top: "0",
    bottom: "0",
    width: "100%",
    height: "100%",
  },
  videoStyle: {
    // minWidth: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
    // minHeight: "100%",

    /* Setting width & height to auto prevents the browser from stretching or squishing the video */
    width: "auto",
    height: "auto",

    /* Center the video */
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
  },
  bringToFront: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    zIndex: 1,
  },
});
